import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Receipt, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { DocumentFlowButton } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];

export default function PurchaseBills() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const { companies } = useCompany() || {};
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [billRes, vendorRes, poRes, grnRes] = await Promise.all([
        api.get("/procurement/purchase-bills"), api.get("/procurement/vendors"),
        api.get("/procurement/purchase-orders"), api.get("/procurement/grns"),
      ]);
      setBills(billRes.data); setVendors(vendorRes.data); setPos(poRes.data); setGrns(grnRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";

  const remove = async (bill) => {
    if (!window.confirm(`Delete draft ${bill.number}? Nothing has been posted, so nothing is reversed.`)) return;
    setBusyId(bill.id);
    try {
      await api.delete(`/procurement/purchase-bills/${bill.id}`);
      toast.success("Draft bill deleted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const post = async (bill) => {
    if (!bill.vendor_bill_no && !window.confirm("This bill has no vendor bill number yet. Post anyway?")) return;
    setBusyId(bill.id);
    try {
      await api.post(`/procurement/purchase-bills/${bill.id}/post`);
      toast.success("Bill posted to GL (Accounts Payable)");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="purchase-bills-page">
      <PageHeader title="Purchase Bills" description="Vendor invoices against a PO/GRN. Posting clears GRIR and credits Accounts Payable." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canManage && <CreateBillDialog companies={companies} vendors={vendors} pos={pos} grns={grns} bills={bills} onCreated={load} />}</div>} />
      {(() => {
        const open = pf.apply(bills, (b) => b.project_id).filter((b) => b.status === "POSTED" && Number(b.total_amount) - Number(b.paid_amount) - Number(b.adjusted_amount || 0) > 0.005);
        const outstanding = open.reduce((sum, b) => sum + Number(b.total_amount) - Number(b.paid_amount) - Number(b.adjusted_amount || 0), 0);
        return bills.length > 0 && (
          <div className="flex flex-wrap gap-3 text-sm" data-testid="bills-outstanding-summary">
            <div className="rounded-lg border border-border bg-card px-3 py-2"><span className="text-muted-foreground">Outstanding: </span><span className="font-mono font-semibold text-foreground">₹{inrAmount(outstanding)}</span></div>
            <div className="rounded-lg border border-border bg-card px-3 py-2"><span className="text-muted-foreground">Unpaid / partly paid bills: </span><span className="font-semibold text-foreground">{open.length}</span></div>
          </div>
        );
      })()}
      <div className="rounded-xl border border-border bg-card">
        {!loading && bills.length === 0 ? (
          <EmptyState title="No purchase bills yet" testId="purchase-bills-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Vendor</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {pf.apply(bills, (r) => r.project_id).map((bill) => (
                <TableRow key={bill.id} data-testid={`bill-row-${bill.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">
                    {bill.number}
                    {bill.status === "DRAFT" && !bill.vendor_bill_no && <div className="font-sans text-[11px] text-amber-600 dark:text-amber-400">Vendor bill no. missing</div>}
                    {bill.status === "DRAFT" && (bill.remarks || "").startsWith("Auto-drafted") && <div className="font-sans text-[10px] text-sky-600 dark:text-sky-400">Auto from GRN</div>}
                  </TableCell>
                  <TableCell className="text-foreground">{vendorName(bill.vendor_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{bill.bill_date}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{inrAmount(bill.total_amount)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{bill.status === "POSTED" ? inrAmount(bill.paid_amount) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{bill.status === "POSTED" ? inrAmount(Number(bill.total_amount) - Number(bill.paid_amount) - Number(bill.adjusted_amount || 0)) : "—"}
                    {Number(bill.adjusted_amount) > 0 && <div className="text-[10px] font-normal text-muted-foreground">after debit notes −{inrAmount(bill.adjusted_amount)}</div>}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <StatusPill value={bill.status} />
                    {bill.status === "POSTED" && <PaymentBadge due={Number(bill.total_amount)} paid={Number(bill.paid_amount) + Number(bill.adjusted_amount || 0)} />}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage && bill.status === "DRAFT" && (
                      <div className="inline-flex gap-1.5">
                        <EditPurchaseBillDialog bill={bill} pos={pos} grns={grns} onSaved={load} />
                        <Button size="sm" variant="outline" disabled={busyId === bill.id} onClick={() => remove(bill)} title="Delete draft" data-testid={`delete-bill-button-${bill.number}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                        </Button>
                        <Button size="sm" variant="outline" disabled={busyId === bill.id} onClick={() => post(bill)} data-testid={`post-bill-button-${bill.number}`}>
                          <Receipt className="h-3.5 w-3.5" /> Post
                        </Button>
                      </div>
                    )}
                    <DocumentFlowButton docType="PURCHASE_BILL" docId={bill.id} cancelEndpoint={bill.status === "POSTED" ? `/procurement/purchase-bills/${bill.id}/cancel` : undefined} onCancelled={load} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateBillDialog({ companies, vendors, pos, grns, bills, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", vendor_id: "", purchase_order_id: "", grn_id: "", vendor_bill_no: "", bill_date: "", subtotal: "", tax_amount: "0" });
  const [lineRates, setLineRates] = useState({});
  const [lineQtys, setLineQtys] = useState({});
  const [matchOverride, setMatchOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const selectedGRN = grns.find((g) => g.id === form.grn_id);

  // GRNs that can still be billed: posted, and not already on a (non-cancelled) purchase bill
  const billedGrnIds = new Set((bills || []).filter((b) => b.status !== "CANCELLED" && b.grn_id).map((b) => b.grn_id));
  const openGrns = grns.filter((g) => g.status === "POSTED" && !billedGrnIds.has(g.id));
  const poLine = (grnItem) => pos.flatMap((p) => p.items || []).find((i) => i.id === grnItem.po_item_id);
  const poNumber = (id) => pos.find((p) => p.id === id)?.number || "—";

  const selectGRN = (id) => {
    const grn = grns.find((g) => g.id === id);
    const po = pos.find((p) => p.id === grn?.purchase_order_id);
    const qtys = {};
    const rates = {};
    let subtotal = 0;
    for (const it of grn?.items || []) {
      const line = pos.flatMap((p) => p.items || []).find((i) => i.id === it.po_item_id);
      qtys[it.id] = String(Number(it.quantity_received));
      rates[it.id] = line ? String(line.rate) : "";
      subtotal += Number(it.quantity_received) * Number(line?.rate || 0);
    }
    const taxPct = po && Number(po.subtotal) ? Number(po.tax_amount) / Number(po.subtotal) : 0;
    setForm({
      ...form, grn_id: id,
      purchase_order_id: po?.id || form.purchase_order_id,
      vendor_id: po?.vendor_id || form.vendor_id,
      company_id: grn?.company_id || po?.company_id || form.company_id,
      tax_amount: (subtotal * taxPct).toFixed(2),
    });
    setLineQtys(qtys);
    setLineRates(rates);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, purchase_order_id: form.purchase_order_id || null, grn_id: form.grn_id || null, match_override: matchOverride, match_override_reason: overrideReason || null };
      if (selectedGRN) {
        payload.items = selectedGRN.items
          .filter((it) => lineQtys[it.id])
          .map((it) => ({ grn_item_id: it.id, description: poLine(it)?.description || `GRN line ${it.id.slice(0, 8)}`, quantity: lineQtys[it.id], rate: lineRates[it.id] || "0" }));
        delete payload.subtotal;
      }
      await api.post("/procurement/purchase-bills", payload);
      toast.success("Purchase bill created");
      setForm({ company_id: "", vendor_id: "", purchase_order_id: "", grn_id: "", vendor_bill_no: "", bill_date: "", subtotal: "", tax_amount: "0" });
      setLineRates({}); setLineQtys({}); setMatchOverride(false); setOverrideReason("");
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-bill-button"><Plus className="h-4 w-4" /> New Purchase Bill</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Purchase Bill</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-bill-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="bill-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor</Label>
              <Select required value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="bill-vendor-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Against PO (optional)</Label>
              <Select value={form.purchase_order_id} onValueChange={(v) => setForm({ ...form, purchase_order_id: v })}>
                <SelectTrigger data-testid="bill-po-select" className="mt-1.5"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{(pos || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Against GRN (optional — enables 3-way match)</Label>
              <Select value={form.grn_id} onValueChange={selectGRN}>
                <SelectTrigger data-testid="bill-grn-select" className="mt-1.5"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{openGrns.map((g) => <SelectItem key={g.id} value={g.id}>{g.number} · {poNumber(g.purchase_order_id)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vendor bill no.</Label><Input data-testid="bill-vendor-bill-no-input" value={form.vendor_bill_no} onChange={set("vendor_bill_no")} className="mt-1.5" /></div>
            <div><Label>Bill date</Label><Input required type="date" data-testid="bill-date-input" value={form.bill_date} onChange={set("bill_date")} className="mt-1.5" /></div>
          </div>
          {selectedGRN ? (
            <div className="space-y-2" data-testid="bill-grn-items">
              <Label>Billed qty & rate per GRN line (checked against qty received &amp; PO rate)</Label>
              {selectedGRN.items.map((it) => (
                <div key={it.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 text-muted-foreground">{poLine(it)?.description || "Item"} (received {Number(it.quantity_received)}, PO rate {poLine(it)?.rate ?? "—"})</span>
                  <Input type="number" step="0.001" placeholder="Qty" className="w-24" value={lineQtys[it.id] || ""} onChange={(e) => setLineQtys({ ...lineQtys, [it.id]: e.target.value })} data-testid={`bill-grn-item-qty-input-${it.id}`} />
                  <Input type="number" step="0.01" placeholder="Rate" className="w-24" value={lineRates[it.id] || ""} onChange={(e) => setLineRates({ ...lineRates, [it.id]: e.target.value })} data-testid={`bill-grn-item-rate-input-${it.id}`} />
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <input type="checkbox" id="match-override" checked={matchOverride} onChange={(e) => setMatchOverride(e.target.checked)} data-testid="bill-match-override-checkbox" />
                <Label htmlFor="match-override" className="!mb-0 text-xs font-normal">Override the 3-way match (qty/rate outside tolerance)</Label>
              </div>
              {matchOverride && <Input placeholder="Reason for override" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} data-testid="bill-match-override-reason-input" />}
              <div><Label>Tax amount</Label><Input type="number" step="0.01" data-testid="bill-tax-input" value={form.tax_amount} onChange={set("tax_amount")} className="mt-1.5 w-32" /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subtotal</Label><Input required type="number" step="0.01" data-testid="bill-subtotal-input" value={form.subtotal} onChange={set("subtotal")} className="mt-1.5" /></div>
              <div><Label>Tax amount</Label><Input type="number" step="0.01" data-testid="bill-tax-input" value={form.tax_amount} onChange={set("tax_amount")} className="mt-1.5" /></div>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-bill-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentBadge({ due, paid }) {
  if (due <= 0) return null;
  const balance = due - paid;
  const [label, tone] =
    balance <= 0.005
      ? ["Paid", "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"]
      : paid > 0
        ? ["Partly paid", "bg-amber-500/10 text-amber-600 dark:text-amber-400"]
        : ["Unpaid", "bg-rose-500/10 text-rose-600 dark:text-rose-400"];
  return <span className={`ml-1.5 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>;
}

const inrAmount = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function EditPurchaseBillDialog({ bill, pos, grns, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [lines, setLines] = useState([]);
  const [override, setOverride] = useState({ on: false, reason: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const poLines = (pos || []).flatMap((p) => p.items || []);
  const grnItems = (grns || []).flatMap((g) => g.items || []);

  useEffect(() => {
    if (!open) return;
    setForm({ vendor_bill_no: bill.vendor_bill_no || "", bill_date: bill.bill_date, tax_amount: String(bill.tax_amount), remarks: bill.remarks || "" });
    setLines((bill.items || []).map((i) => ({ grn_item_id: i.grn_item_id, description: i.description, quantity: String(Number(i.quantity)), rate: String(i.rate) })));
    setOverride({ on: false, reason: "" });
  }, [open, bill]);

  const lineInfo = (l) => {
    const gi = grnItems.find((g) => g.id === l.grn_item_id);
    const pl = gi ? poLines.find((p) => p.id === gi.po_item_id) : null;
    return { received: gi ? Number(gi.quantity_received) : null, poRate: pl ? Number(pl.rate) : null };
  };
  const subtotal = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.rate || 0), 0);
  const po = (pos || []).find((p) => p.id === bill.purchase_order_id);
  const gstPct = po && Number(po.subtotal) ? (Number(po.tax_amount) / Number(po.subtotal)) * 100 : 0;

  const recalcTax = () => setForm({ ...form, tax_amount: ((subtotal * gstPct) / 100).toFixed(2) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/procurement/purchase-bills/${bill.id}`, {
        vendor_bill_no: form.vendor_bill_no || null, bill_date: form.bill_date, tax_amount: form.tax_amount || "0", remarks: form.remarks || null,
        items: lines.filter((l) => Number(l.quantity) > 0),
        match_override: override.on, match_override_reason: override.on ? override.reason : null,
      });
      toast.success("Purchase bill updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" title="Edit draft" data-testid={`edit-bill-button-${bill.number}`}><Pencil className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto overflow-x-hidden sm:max-w-xl">
        <DialogHeader><DialogTitle>Edit {bill.number}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <p className="text-xs text-muted-foreground">Match this to the vendor's invoice. If their rate or quantity differs from the PO / GRN, change it here — a higher rate or extra quantity will create a debit note draft when you Post.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vendor bill no.</Label><Input value={form.vendor_bill_no || ""} onChange={set("vendor_bill_no")} className="mt-1.5" data-testid="edit-bill-vendor-bill-no" /></div>
            <div><Label>Bill date</Label><Input required type="date" value={form.bill_date || ""} onChange={set("bill_date")} className="mt-1.5" /></div>
          </div>
          <div className="space-y-2">
            <Label>Lines</Label>
            {lines.map((l, idx) => {
              const info = lineInfo(l);
              return (
                <div key={idx} className="rounded-lg border border-border p-2 text-sm">
                  <div className="truncate text-foreground">{l.description}</div>
                  <div className="text-[11px] text-muted-foreground">{info.received != null ? `received ${info.received}` : ""}{info.poRate != null ? ` · PO rate ₹${fmt(info.poRate)}` : ""}</div>
                  <div className="mt-1.5 grid grid-cols-3 items-center gap-2">
                    <Input type="number" step="0.001" min="0" value={l.quantity} onChange={(e) => setLines(lines.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x)))} placeholder="Qty" />
                    <Input type="number" step="0.01" min="0" value={l.rate} onChange={(e) => setLines(lines.map((x, i) => (i === idx ? { ...x, rate: e.target.value } : x)))} placeholder="Rate" />
                    <span className="text-right font-mono text-xs text-foreground">₹{fmt(Number(l.quantity || 0) * Number(l.rate || 0))}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 items-end gap-3">
            <div>
              <Label>GST amount (₹)</Label>
              <Input type="number" step="0.01" min="0" value={form.tax_amount ?? ""} onChange={set("tax_amount")} className="mt-1.5" />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={recalcTax}>Recalculate at PO GST ({gstPct.toFixed(0)}%)</Button>
          </div>
          <div className="rounded-lg bg-muted/50 p-3 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono">₹{fmt(subtotal)}</span></div>
            <div className="flex justify-between font-semibold text-foreground"><span>Total</span><span className="font-mono">₹{fmt(subtotal + Number(form.tax_amount || 0))}</span></div>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={override.on} onChange={(e) => setOverride({ ...override, on: e.target.checked })} />
            The vendor's invoice really differs from PO/GRN beyond tolerance (record why)
          </label>
          {override.on && <Input required placeholder="Reason, e.g. agreed rate revision" value={override.reason} onChange={(e) => setOverride({ ...override, reason: e.target.value })} />}
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
